import { DECK, SUITS, isCard, isPlayable, isSuit, isTwo, pointsOf, strength, trumpOrder, type Card } from './cards';
import { rngNext } from './rng';
import {
  DEFAULT_RULES,
  MAX_PLAYERS,
  MIN_PLAYERS,
  MOON_BID,
  MOON_SCORE,
  biddersLeft,
  bidOptions,
  calledHolder,
  givableCards,
  highBid,
  keepRule,
  kittySizeFor,
  nextSeat,
  partnersEnabled,
  playableIn,
  pointsNeeded,
} from './rules';
import type { Action, GameEvent, GameState, HandResult, HandState, RulesConfig } from './types';

export interface CreateGameOptions {
  playerCount: number;
  seed: number;
  rules?: Partial<RulesConfig>;
  /** Seat that deals the first hand (bidding starts to their left). */
  dealer?: number;
}

function isIndex(x: unknown, count: number): x is number {
  return typeof x === 'number' && Number.isInteger(x) && x >= 0 && x < count;
}

function random(state: GameState): number {
  const [value, next] = rngNext(state.rng);
  state.rng = next;
  return value;
}

function emptyHand(playerCount: number, number: number, dealer: number): HandState {
  const each = <T>(f: () => T) => Array.from({ length: playerCount }, f);
  return {
    number,
    dealer,
    hands: each(() => []),
    kitty: [],
    kittyCards: [],
    discards: each(() => []),
    bids: each(() => null),
    bidSuits: each(() => null),
    bidder: null,
    bid: null,
    trump: null,
    called: null,
    partner: null,
    passedCount: null,
    hasPlayed: each(() => false),
    trick: null,
    lastTrick: null,
    won: each(() => []),
    points: each(() => 0),
  };
}

function dealHand(state: GameState, number: number, dealer: number, events: GameEvent[]): void {
  const n = state.config.playerCount;
  const deck = DECK.slice();
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(random(state) * (i + 1));
    [deck[i], deck[j]] = [deck[j]!, deck[i]!];
  }
  const hand = emptyHand(n, number, dealer);
  const k = kittySizeFor(n, state.config.rules);
  hand.kitty = deck.slice(0, k);
  hand.kittyCards = hand.kitty.slice();
  deck.slice(k).forEach((card, i) => hand.hands[(dealer + 1 + i) % n]!.push(card));
  state.hand = hand;
  state.phase = { kind: 'bid', turn: nextSeat(state.config, dealer) };
  events.push({ type: 'handStarted', hand: number, dealer });
}

export function createGame(opts: CreateGameOptions): GameState {
  const { playerCount } = opts;
  if (!Number.isInteger(playerCount) || playerCount < MIN_PLAYERS || playerCount > MAX_PLAYERS) {
    throw new Error(`playerCount must be ${MIN_PLAYERS}–${MAX_PLAYERS}`);
  }
  const rules = { ...DEFAULT_RULES, ...opts.rules };
  const state: GameState = {
    config: { playerCount, rules },
    rng: opts.seed >>> 0,
    scores: Array(playerCount).fill(0),
    hand: emptyHand(playerCount, 0, 0),
    phase: { kind: 'bid', turn: 0 },
    history: [],
  };
  dealHand(state, 1, opts.dealer ?? 0, []);
  return state;
}

export type ActionResult = { ok: true; state: GameState; events: GameEvent[] } | { ok: false; error: string };

/** Pure reducer: returns a new state (the input is never mutated) or an error. */
export function applyAction(prev: GameState, player: number, action: Action): ActionResult {
  const error = validate(prev, player, action);
  if (error !== null) return { ok: false, error };
  const state = structuredClone(prev);
  const events: GameEvent[] = [];
  execute(state, player, action, events);
  return { ok: true, state, events };
}

function distinctCards(x: unknown): x is Card[] {
  return Array.isArray(x) && x.every(isCard) && new Set(x).size === x.length;
}

// ---------------------------------------------------------------------------
// Validation (read-only). Returns an error message, or null if legal.

export function validate(state: GameState, player: number, action: Action): string | null {
  if (!isIndex(player, state.config.playerCount)) return 'unknown player';
  const { phase, hand } = state;
  if (phase.kind === 'gameOver') return 'the game is over';

  switch (action.type) {
    case 'bid':
      if (phase.kind !== 'bid') return 'bidding is over';
      if (player !== phase.turn) return 'not your turn to bid';
      if (!bidOptions(state, player).includes(action.amount)) {
        return action.amount === 0 ? 'everyone else passed, so you must bid' : 'you cannot bid that';
      }
      if (action.amount === 0) return action.suit === undefined ? null : 'a pass has no suit';
      return isSuit(action.suit) ? null : 'a bid names its suit';

    case 'call': {
      if (phase.kind !== 'call') return 'it is not time to call';
      if (player !== hand.bidder) return 'only the bidder calls a partner';
      if (!isCard(action.card)) return 'call a card';
      return isPlayable(action.card, hand.trump!) ? null : 'the called card must be playable';
    }

    case 'give': {
      if (phase.kind !== 'pass') return 'it is not time to pass cards';
      if (player !== hand.bidder) return 'only the bidder passes cards';
      if (!distinctCards(action.cards)) return 'invalid cards';
      const givable = givableCards(state);
      return action.cards.every((c) => givable.includes(c)) ? null : 'you can only pass playable cards from the kitty';
    }

    case 'keep': {
      if (phase.kind !== 'trim') return 'it is not time to choose your cards';
      if (!phase.pending[player]) return 'you already chose your cards';
      if (!distinctCards(action.cards)) return 'invalid cards';
      const rule = keepRule(state, player);
      if (action.cards.length !== rule.count) return `keep exactly ${rule.count} cards`;
      if (!action.cards.every((c) => rule.options.includes(c))) return 'you can only keep playable cards you hold';
      return rule.required.every((c) => action.cards.includes(c)) ? null : 'you must keep the called card';
    }

    case 'play': {
      if (phase.kind !== 'play') return 'it is not time to play';
      if (player !== phase.turn) return 'not your turn';
      if (!isCard(action.card) || !hand.hands[player]!.includes(action.card)) return 'you do not have that card';
      const mustLeadCalled = hand.called !== null && !hand.hasPlayed[player] && hand.hands[player]!.includes(hand.called);
      if (mustLeadCalled && action.card !== hand.called) return 'your first play must be the called card';
      return null;
    }

    default:
      return 'unknown action';
  }
}

// ---------------------------------------------------------------------------
// Execution. Only called after validate() passed; mutates the cloned state.

function execute(state: GameState, player: number, action: Action, events: GameEvent[]): void {
  const hand = state.hand;
  switch (action.type) {
    case 'bid': {
      hand.bids[player] = action.amount;
      hand.bidSuits[player] = action.amount === 0 ? null : action.suit!;
      events.push(action.amount === 0 ? { type: 'bid', player, amount: 0 } : { type: 'bid', player, amount: action.amount, suit: action.suit! });
      // Bidding goes around until everyone but the high bidder has passed. Nobody can top the moon.
      const left = biddersLeft(hand);
      const high = highBid(hand);
      if (action.amount === MOON_BID || (left.length === 1 && high > 0)) {
        const bidder = hand.bids.indexOf(high);
        hand.bidder = bidder;
        hand.bid = high;
        hand.trump = hand.bidSuits[bidder]!;
        events.push({ type: 'bidWon', player: bidder, bid: high, suit: hand.trump });
        if (partnersEnabled(state.config)) state.phase = { kind: 'call' };
        else {
          takeKitty(state, events);
          startTrim(state, events);
        }
      } else {
        let next = nextSeat(state.config, player);
        while (hand.bids[next] === 0) next = nextSeat(state.config, next);
        state.phase = { kind: 'bid', turn: next };
      }
      return;
    }

    case 'call': {
      const bidder = hand.bidder!;
      hand.called = action.card;
      // Decided before the bidder sees the kitty: a called card in the kitty
      // (or the bidder's own hand) means playing alone. Its holder speaks up right away.
      const holder = hand.hands.findIndex((h) => h.includes(action.card));
      hand.partner = holder >= 0 && holder !== bidder ? holder : null;
      events.push({ type: 'called', player: bidder, card: action.card });
      events.push({ type: 'partnerRevealed', partner: hand.partner, bidder, card: action.card });
      takeKitty(state, events);
      if (hand.partner !== null && givableCards(state).length > 0) state.phase = { kind: 'pass' };
      else startTrim(state, events);
      return;
    }

    case 'give': {
      const bidder = hand.bidder!;
      hand.hands[bidder] = hand.hands[bidder]!.filter((c) => !action.cards.includes(c));
      hand.hands[hand.partner!]!.push(...action.cards);
      hand.passedCount = action.cards.length;
      events.push({ type: 'gave', player: bidder, count: action.cards.length });
      startTrim(state, events);
      return;
    }

    case 'keep': {
      if (state.phase.kind !== 'trim') return;
      trimTo(state, player, action.cards);
      const pending = state.phase.pending.slice();
      pending[player] = false;
      events.push({ type: 'kept', player, count: action.cards.length });
      if (pending.some(Boolean)) state.phase = { kind: 'trim', pending };
      else startPlay(state, events);
      return;
    }

    case 'play':
      playCard(state, player, action.card, events);
      return;
  }
}

function takeKitty(state: GameState, events: GameEvent[]): void {
  const hand = state.hand;
  const bidder = hand.bidder!;
  hand.hands[bidder]!.push(...hand.kitty);
  events.push({ type: 'kittyTaken', player: bidder, count: hand.kitty.length });
  hand.kitty = [];
}

function trimTo(state: GameState, player: number, keep: readonly Card[]): void {
  const hand = state.hand;
  const held = hand.hands[player]!;
  hand.hands[player] = held.filter((c) => keep.includes(c));
  hand.discards[player] = held.filter((c) => !keep.includes(c));
}

/** Players with no more playable cards than the limit keep them all without being asked. */
function startTrim(state: GameState, events: GameEvent[]): void {
  const trump = state.hand.trump!;
  const pending = state.hand.hands.map((held, player) => {
    const playable = playableIn(held, trump);
    if (playable.length > state.config.rules.handLimit) return true;
    trimTo(state, player, playable);
    events.push({ type: 'kept', player, count: playable.length, auto: true });
    return false;
  });
  if (pending.some(Boolean)) state.phase = { kind: 'trim', pending };
  else startPlay(state, events);
}

function firstWithCards(state: GameState, from: number): number | null {
  const n = state.config.playerCount;
  for (let i = 0; i < n; i++) {
    const seat = (from + i) % n;
    if (state.hand.hands[seat]!.length > 0) return seat;
  }
  return null;
}

function startTrick(state: GameState, leader: number): void {
  const n = state.config.playerCount;
  const order: number[] = [];
  for (let i = 0; i < n; i++) {
    const seat = (leader + i) % n;
    if (state.hand.hands[seat]!.length > 0) order.push(seat);
  }
  state.hand.trick = { leader, order, plays: [] };
  state.phase = { kind: 'play', turn: leader };
}

function startPlay(state: GameState, events: GameEvent[]): void {
  // Whoever holds the called card leads it (validate allows nothing else), and play goes round from there.
  const leader = firstWithCards(state, calledHolder(state.hand) ?? state.hand.bidder!);
  if (leader === null) return scoreHand(state, events);
  startTrick(state, leader);
}

function playCard(state: GameState, player: number, card: Card, events: GameEvent[]): void {
  const hand = state.hand;
  const trick = hand.trick!;
  hand.hands[player] = hand.hands[player]!.filter((c) => c !== card);
  hand.hasPlayed[player] = true;
  trick.plays.push({ player, card });
  events.push({ type: 'played', player, card });

  if (trick.plays.length < trick.order.length) {
    state.phase = { kind: 'play', turn: trick.order[trick.plays.length]! };
    return;
  }

  const trump = hand.trump!;
  const winner = trick.plays.reduce((best, p) => (strength(p.card, trump) > strength(best.card, trump) ? p : best)).player;
  let captured = 0;
  for (const p of trick.plays) {
    if (isTwo(p.card, trump)) {
      hand.points[p.player]! += pointsOf(p.card, trump);
      hand.won[p.player]!.push(p.card);
    } else {
      captured += pointsOf(p.card, trump);
      hand.won[winner]!.push(p.card);
    }
  }
  hand.points[winner]! += captured;
  hand.lastTrick = { plays: trick.plays.slice(), winner };
  hand.trick = null;
  events.push({ type: 'trickWon', player: winner, cards: trick.plays.map((p) => p.card), points: captured });

  const leader = firstWithCards(state, winner);
  if (leader === null) scoreHand(state, events);
  else startTrick(state, leader);
}

export function scoreResult(state: GameState): HandResult {
  const hand = state.hand;
  const n = state.config.playerCount;
  const bidder = hand.bidder!;
  const bid = hand.bid!;
  const side = (seat: number) => seat === bidder || seat === hand.partner;
  let bidderSidePoints = 0;
  let defenderPoints = 0;
  for (let i = 0; i < n; i++) {
    if (side(i)) bidderSidePoints += hand.points[i]!;
    else defenderPoints += hand.points[i]!;
  }
  const moon = bid === MOON_BID;
  const made = bidderSidePoints >= pointsNeeded(bid);
  const sideDelta = moon ? (made ? MOON_SCORE : -MOON_SCORE) : made ? bidderSidePoints : -bid;
  return {
    hand: hand.number,
    dealer: hand.dealer,
    bidder,
    bid,
    trump: hand.trump!,
    called: hand.called,
    partner: hand.partner,
    points: hand.points.slice(),
    bidderSidePoints,
    defenderPoints,
    made,
    deltas: Array.from({ length: n }, (_, i) => (side(i) ? sideDelta : defenderPoints)),
  };
}

function scoreHand(state: GameState, events: GameEvent[]): void {
  const result = scoreResult(state);
  state.scores = state.scores.map((s, i) => s + result.deltas[i]!);
  state.history.push(result);
  events.push({ type: 'handScored', result });

  const best = Math.max(...state.scores);
  if (best >= state.config.rules.targetScore) {
    const winners = state.scores.flatMap((s, i) => (s === best ? [i] : []));
    state.phase = { kind: 'gameOver', winners };
    events.push({ type: 'gameOver', winners });
    return;
  }
  dealHand(state, state.hand.number + 1, nextSeat(state.config, state.hand.dealer), events);
}

// ---------------------------------------------------------------------------

/**
 * Every concrete legal action for `player`. Open-ended actions are not
 * enumerated: `give` (see givableCards) and `keep` (see keepRule).
 */
export function legalActions(state: GameState, player: number): Action[] {
  const phase = state.phase;
  const candidates: Action[] = [];
  if (phase.kind === 'bid' && phase.turn === player) {
    for (const amount of bidOptions(state, player)) {
      if (amount === 0) candidates.push({ type: 'bid', amount });
      else for (const suit of SUITS) candidates.push({ type: 'bid', amount, suit });
    }
  } else if (phase.kind === 'call' && state.hand.bidder === player) {
    for (const card of trumpOrder(state.hand.trump!)) candidates.push({ type: 'call', card });
  } else if (phase.kind === 'play' && phase.turn === player) {
    for (const card of state.hand.hands[player]!) candidates.push({ type: 'play', card });
  }
  return candidates.filter((a) => validate(state, player, a) === null);
}
